import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { PCL_CARD_EXPIRY_LIMIT_IN_MONTHS } from '../../utils/credit-card'
import { Button, Checkbox, Divider, Heading, Message, Text, useIsBreakpointLargeAndUp, useModal } from '@afterpaytouch/core'
import {
  ApplePayStatus,
  ApplePayError,
  CheckoutStatus,
  isCardScanRequiredErrorCode,
  NotificationError,
  PaymentMethodType,
  RecoverableErrorCode,
  PaymentSourceCaptureCardTypes,
  AutopayConfirmationModalKey,
  PayMonthlyTILAModalKey,
  Features,
} from '../../model'
import {
  ConsumerLendingPaymentSchedule,
  ConsumerLendingSummaryBreakdown,
  DisabledModal,
  Head,
  InlinePaymentMethod,
  NoErrorStatusGuard,
  OrderSummary,
  ShippingSummary,
  StartedStatusGuard,
  useSummaryNotification,
} from '../../component'
import { PaymentOptionsModal } from '../../component/PaymentOptionsModal'
import { create } from 'react-modal-promise'
import { Country, ErrorCode } from '@afterpaytouch/portal-api'
import { Route } from '../../config/router'
import { useCheckoutSizes } from '../../utils/responsive'
import { useTranslation } from '../../utils/convergence'
import {
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useCountry,
  useEnforcePclCardExpiryLimit,
  useIsDeferredShipping,
  useIsExpress,
  useIsPickup,
  useIsReadyToConfirm,
  usePreferredBankAccount,
  usePreferredCard,
  usePreferredPaymentMethod,
  useSelectedPaymentMethod,
  useApplePayState,
  useApplePayFeature,
  useIsAuNonAppleRetailCheckout,
  resetCheckoutError,
  setConfirmTermsViewed,
  useCachedFeatureFlag,
} from '../../state'
import styles from '../summary-pay-monthly/style.module.css'
import redesignStyles from './style.module.css'
import { AutopayButton, AutopayModal } from '../../component/AutopayToggle'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { NonTerminalErrors, TrackingEvent } from '../../model/monitoring'
import { useGetIsAutoPayAgreed } from '../../component/AutopayToggle/hooks'
import { Terms as TILATerms } from '../../component/TruthInLending/Terms'
import { TruthInLendingModal } from '../../component/TruthInLending/TruthInLendingModal'
import { TruthInLendingButton } from '../../component/TruthInLending/TruthInLendingButton'
import { isPayMonthlyConfirmationBlocked } from '../summary-pay-monthly/isPayMonthlyConfirmationBlocked'

export const PAY_MONTHLY_SUMMARY_VERSION_REDESIGN = 'pm redesign v1'

export const TILA_TERMS_ERROR_ID = 'tila-terms-checkbox-error'

export const PayMonthlySummaryRedesignedPage: FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const { status, error } = useCheckoutState()
  const applePay = useApplePayState()
  const { sectionHeadingSize, headingSize, textSize, smallTextSize } = useCheckoutSizes()
  const ready = useIsReadyToConfirm()
  const country = useCountry()
  const requiresPayMonthlyAutopayControls = country === Country.US
  const requiresPayMonthlyTermsConsent = country !== Country.AU
  const [cardExpiryMonthsEventSent, setCardExpiryMonthsEventSent] = useState<boolean>(false)
  const [notAcceptedPaymentMethodEventSent, setnotAcceptedPaymentMethodEventSent] = useState<boolean>(false)
  const [clicked, setClicked] = useState<boolean>(false)
  const [notification, setNotification] = useState<NotificationError>()
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const { account, valid: bankAccountValid, reason: bankAccountReason } = usePreferredBankAccount() ?? {}
  const { card, valid, reason, switched } = usePreferredCard() ?? {}
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const { notification: inlineNotification } = useSummaryNotification()
  useEnforcePclCardExpiryLimit()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isDesktop = useIsBreakpointLargeAndUp()
  const isPickup = useIsPickup()
  const isExpress = useIsExpress()
  const isDeferredShipping = useIsDeferredShipping()
  const cardExpiryLimitInMonths = PCL_CARD_EXPIRY_LIMIT_IN_MONTHS
  const applePayFeature = useApplePayFeature()
  const cashPayMonthlyEnabled = useCachedFeatureFlag(Features.CONVERGENCE_CASH_PAY_MONTHLY_ENABLED, false)
  const { showModal: showAutopayConfirmationModal } = useModal(AutopayConfirmationModalKey)
  const autoPayAgreed = useGetIsAutoPayAgreed() ?? false
  const isAuNonAppleRetailCheckout = useIsAuNonAppleRetailCheckout()
  const requiresAutopayConfirmation = requiresPayMonthlyAutopayControls && autoPayAgreed
  const [documentId, setDocumentId] = useState<string>('')
  const [tilDisclosure, setTilDisclosure] = useState<boolean>(false)
  const [isTILATermsCheckboxChecked, setTILATermsCheckboxChecked] = useState<boolean>(false)
  const [showTILACheckboxErrorMessage, setShowTILACheckboxErrorMessage] = useState<boolean>(false)
  const { showModal: showPayMonthlyTILAModal } = useModal(PayMonthlyTILAModalKey)
  const showPaymentOptionsModal = create(PaymentOptionsModal)
  const hasBlockingPaymentError = isPayMonthlyConfirmationBlocked({
    applePayEnabledForConsumerLending: applePayFeature.enabledForConsumerLending,
    applePayError: applePay.error,
    bankAccountReason,
    bankAccountValid,
    cardReason: reason,
    cardValid: valid,
    cashPayMonthlyEnabled,
    hasBankAccount: account != null,
    hasCard: card != null,
    preferredPaymentMethod,
    selectedPaymentMethodType: selectedPaymentMethod?.type,
  })

  const setDocumentIdToShow = (documentId: string, isTilDisclosure: boolean = false): void => {
    if ((documentId?.trim().length ?? 0) === 0) {
      return
    }
    showPayMonthlyTILAModal()
    setTilDisclosure(isTilDisclosure)
    setDocumentId(documentId)
  }

  const handleTILATermsCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const isChecked = e.target.checked
    setTILATermsCheckboxChecked(isChecked)
    if (isChecked) {
      setShowTILACheckboxErrorMessage(false)
    }
  }

  const renderTILACheckboxErrorMessage = (): ReactNode | null => {
    if (!requiresPayMonthlyTermsConsent || !showTILACheckboxErrorMessage) {
      return null
    }

    return (
      <div className={styles.tilaCheckboxErrorMessageContainer}>
        <Text color='Fire' size={smallTextSize} id={TILA_TERMS_ERROR_ID} role='alert'>
          {t('consumerLending:til.acceptToContinue')}
        </Text>
      </div>
    )
  }

  const handleButtonClick = async (): Promise<void> => {
    if (isTILATermsCheckboxChecked && requiresAutopayConfirmation) {
      showAutopayConfirmationModal()
    } else {
      setShowTILACheckboxErrorMessage(true)
    }
  }

  useEffect(() => {
    if (!requiresPayMonthlyTermsConsent) {
      dispatch(setConfirmTermsViewed(null))
    }
  }, [dispatch, requiresPayMonthlyTermsConsent])

  useEffect(() => {
    if (error?.extra !== undefined) {
      const { extra } = error
      if (extra.isEFAFinished) {
        dispatch(resetCheckoutError())
      }
    }
  }, [error])

  useEffect(() => {
    if (applePay.status === ApplePayStatus.Cancelled) {
      setClicked(false)
    }
  }, [applePay.status])

  useEffect(() => {
    if (status <= CheckoutStatus.Loaded) {
      router.push(Route.LOGGED_IN)
    }

    if (status === CheckoutStatus.IdentityIneligible) {
      if (country === Country.GB) {
        router.push(Route.VERIFY_RETRY)
      } else {
        router.push(Route.VERIFY_IDENTITY)
      }
    }

    if (status === CheckoutStatus.LackOfEligiblePaymentMethod) {
      router.push(Route.ADD_PAYMENT_METHOD)
    }

    router.prefetch(Route.PAYMENT_METHOD)
  }, [country, router, status])

  useEffect(() => {
    if (error?.error !== 'undefined' && isCardScanRequiredErrorCode(error?.error as ErrorCode)) {
      return setClicked(false)
    } else if (typeof error?.error !== 'undefined' && Object.values(RecoverableErrorCode).includes(error?.error as RecoverableErrorCode)) {
      setClicked(false)
      setNotification({
        heading: t(`summary:error.${error?.error}.heading`),
        message: t(`summary:error.${error?.error}.message`),
        severe: true,
      })
    } else if (applePay.error !== undefined) {
      setClicked(false)
      if (applePay?.error === ApplePayError.InvalidCardType) {
        if (applePay?.cardType === PaymentSourceCaptureCardTypes.CREDIT) {
          setNotification({
            heading: t('summary:error.creditCardProhibitedForPaymentType.heading'),
            message: t('summary:error.creditCardProhibitedForPaymentType.message'),
            severe: false,
          })
        } else {
          setNotification({
            heading: t('summary:error.prepaidCard.heading'),
            message: t('summary:error.prepaidCard.message'),
            severe: false,
          })
        }
      } else if (applePay?.error === ApplePayError.BinBlocked) {
        setNotification({
          heading: t('summary:error.unsupportedCardIssuerBank.heading'),
          message: t('summary:error.unsupportedCardIssuerBank.message'),
          severe: false,
        })
      } else {
        setNotification({
          heading: t('summary:error.applePay.heading'),
          message: t('summary:error.applePay.message'),
          severe: true,
        })
      }
    }
  }, [error, applePay.error])

  useEffect(() => {
    const switchToValidCard = selectedPaymentMethod?.type === PaymentMethodType.CREDIT_CARD && card !== null && card?.error === null
    if (preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && switchToValidCard) {
      setNotification({
        heading: t(`consumerLending:bankAccountErrors.switchedToCard.heading`),
        message: t(`consumerLending:bankAccountErrors.switchedToCard.message`),
        severe: false,
      })
    } else if (
      preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT &&
      account !== null &&
      bankAccountReason !== null &&
      selectedPaymentMethod?.type === PaymentMethodType.ACH_BANK_ACCOUNT
    ) {
      if (!notAcceptedPaymentMethodEventSent) {
        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: bankAccountReason })
        setnotAcceptedPaymentMethodEventSent(true)
      }

      setNotification({
        heading: t(`summary:bankAccountErrors.${bankAccountReason}.heading`),
        message: t(`summary:bankAccountErrors.${bankAccountReason}.message`),
        severe: !bankAccountValid,
      })
    } else if (preferredPaymentMethod === PaymentMethodType.APPLE_PAY && switchToValidCard && !applePayFeature.enabledForConsumerLending) {
      setNotification({
        heading: t(`consumerLending:applePayNotAllowed.heading_switched`),
        message: t(`consumerLending:applePayNotAllowed.message_switched`),
        severe: false,
      })
    } else if (
      preferredPaymentMethod === PaymentMethodType.APPLE_PAY &&
      selectedPaymentMethod?.type === PaymentMethodType.APPLE_PAY &&
      !applePayFeature.enabledForConsumerLending
    ) {
      setNotification({
        heading: t(`consumerLending:applePayNotAllowed.heading_cannotSwitch`),
        message: t(`consumerLending:applePayNotAllowed.message_cannotSwitch`),
        severe: true,
      })
    } else if (preferredPaymentMethod === PaymentMethodType.CASH_APP_PAY && switchToValidCard && !cashPayMonthlyEnabled) {
      setNotification({
        heading: t(`consumerLending:cashAppPayNotAllowed.heading_switched`),
        message: t(`consumerLending:cashAppPayNotAllowed.message_switched`),
        severe: false,
      })
    } else if (
      preferredPaymentMethod === PaymentMethodType.CASH_APP_PAY &&
      selectedPaymentMethod?.type === PaymentMethodType.CASH_APP_PAY &&
      !cashPayMonthlyEnabled
    ) {
      setNotification({
        heading: t(`consumerLending:cashAppPayNotAllowed.heading_cannotSwitch`),
        message: t(`consumerLending:cashAppPayNotAllowed.message_cannotSwitch`),
        severe: true,
      })
    } else if (cardExpiryLimitInMonths !== null && selectedPaymentMethod?.type === PaymentMethodType.CREDIT_CARD && reason === 'cardExpiryLimitInMonths') {
      const options = { context: switched ? 'switched' : 'cannotSwitch', count: cardExpiryLimitInMonths }

      if (!cardExpiryMonthsEventSent) {
        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: NonTerminalErrors.ClCardExpiryError })
        setCardExpiryMonthsEventSent(true)
      }

      setNotification({
        heading: t(`summary:error.cardExpiryLimitInMonths.heading`, options),
        message: t(`summary:error.cardExpiryLimitInMonths.message`, options),
        severe: !valid,
      })
    } else if (card !== null && reason !== null) {
      if (!notAcceptedPaymentMethodEventSent) {
        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: reason })
        setnotAcceptedPaymentMethodEventSent(true)
      }

      const options = { context: switched ? 'switched' : 'cannotSwitch' }
      setNotification({
        heading: t(`summary:error.${reason}.heading`, options),
        message: t(`summary:error.${reason}.message`, options),
        severe: !valid,
      })
    } else if (error == null && applePay.error === undefined) {
      setNotification(undefined)
    }
  }, [
    card,
    valid,
    reason,
    switched,
    account,
    preferredPaymentMethod,
    bankAccountReason,
    selectedPaymentMethod,
    cardExpiryLimitInMonths,
    cashPayMonthlyEnabled,
    error,
    applePay.error,
  ])

  const renderAutopayToggle = (): ReactNode | null => {
    if (!requiresPayMonthlyAutopayControls) {
      return null
    }

    return <AutopayButton />
  }

  const renderCTAButton = (): ReactNode | null => {
    if (!requiresPayMonthlyTermsConsent || (isTILATermsCheckboxChecked && !requiresAutopayConfirmation)) {
      return (
        <TruthInLendingButton
          disabled={!ready || hasBlockingPaymentError || notification?.severe === true}
          isAutopayConfirmation={requiresPayMonthlyAutopayControls}
          loading={clicked}
          onLoadingChange={setClicked}
        />
      )
    }

    return (
      <Button
        padding='Fluid'
        loading={clicked}
        onClick={handleButtonClick}
        testNameSpace='consumer-lending-summary'
        disabled={!ready || clicked || notification?.severe}
      >
        {t('consumerLending:til.cta')}
      </Button>
    )
  }

  return (
    <>
      <NoErrorStatusGuard>
        <Head title={`${t('summary:reviewOrder')} | ${t('summary:brandName')}`} />
        <div className={styles.header}>
          <Heading size={headingSize} renderAs='h1'>
            {isAuNonAppleRetailCheckout ? t('consumerLending:summary.title', { context: 'auPm' }) : t('consumerLending:summary.title')}
          </Heading>
        </div>
        <div className={isDesktop ? redesignStyles.bodyWrapperDesktop : redesignStyles.bodyWrapperMobile}>
          <StartedStatusGuard>
            {isExpress && (
              <>
                <div className={styles.shippingSummary}>
                  {!isDeferredShipping && (
                    <div className={styles.shippingSummaryHeading}>
                      <Heading size={sectionHeadingSize} renderAs='h2'>
                        {isPickup ? t('common:orderCollectionDetails') : t('common:chooseShippingOptions')}
                      </Heading>
                    </div>
                  )}
                  <ShippingSummary hasBorder={false} />
                </div>
              </>
            )}
            <OrderSummary showItemCountHeader />
            <div className={redesignStyles.sectionHeading}>
              <Heading size={sectionHeadingSize} renderAs='h2'>
                {t('consumerLending:summary:paymentSectionTitle')}
              </Heading>
            </div>
            <ConsumerLendingPaymentSchedule
              planSummaryHeader
              detailsFooter={
                <Button.Secondary
                  onClick={() => {
                    logEvent(TrackingEvent.PRESSED_CL_PLAN_EDIT_BUTTON)
                    showPaymentOptionsModal()
                  }}
                  padding='Fluid'
                  testNameSpace='consumer-lending-summary-redesign-change-plan'
                >
                  {t('consumerLending:summary:changePlan')}
                </Button.Secondary>
              }
            />
            <span id='select-payment-method' className={redesignStyles.srOnly}>
              {t('paymentMethod:chooseAPaymentMethod')}
            </span>
            <InlinePaymentMethod />
            {notification !== undefined && inlineNotification === undefined && (
              <div className={styles.notification}>
                {notification.severe ? (
                  <Text color='Fire' size={textSize}>
                    {notification?.message}
                  </Text>
                ) : (
                  <Message kind='Basic' iconName='Info' heading={notification.heading} description={notification?.message} />
                )}
              </div>
            )}
            {error !== undefined && <TrackOnMount eventName={TrackingEvent.VIEWED_NON_TERMINAL_ERROR} eventProperties={{ errorCode: error?.error }} />}
            {renderAutopayToggle()}
            <div className={redesignStyles.sectionDivider}>
              <Divider kind='Hairline' />
            </div>
            <ConsumerLendingSummaryBreakdown />
            <div className={redesignStyles.summaryActions}>
              {requiresPayMonthlyTermsConsent && (
                <Checkbox
                  id='tila-terms-checkbox'
                  error={showTILACheckboxErrorMessage}
                  aria-invalid={showTILACheckboxErrorMessage}
                  aria-describedby={showTILACheckboxErrorMessage ? TILA_TERMS_ERROR_ID : undefined}
                  label={<TILATerms setDocumentIdToShow={setDocumentIdToShow} />}
                  onChange={handleTILATermsCheckboxChange}
                />
              )}
              {renderTILACheckboxErrorMessage()}
              {renderCTAButton()}
            </div>
            {requiresPayMonthlyAutopayControls && <AutopayModal modalKey={AutopayConfirmationModalKey} autoPayAgreed={false} />}
            <TruthInLendingModal documentId={documentId} isTilDisclosure={tilDisclosure} />
            {clicked && <DisabledModal />}
          </StartedStatusGuard>
        </div>
      </NoErrorStatusGuard>
      <TrackOnMount eventName={TrackingEvent.VIEWED_SUMMARY_PAGE} eventProperties={{ summaryPageVersion: PAY_MONTHLY_SUMMARY_VERSION_REDESIGN }} />
    </>
  )
}
